// components/BulkUploadModalContent.tsx
import React, { useState, useRef } from "react";
import {
  List,
  ListItem,
  Box,
  Typography,
  Button,
  LinearProgress,
  ListItemSecondaryAction,
  Grid,
  useTheme,
  useMediaQuery,
  Modal,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { RootState } from "../../services/redux/store";
import { useDispatch, useSelector } from "react-redux";
import PDFViewer from "../../components/ui/PDFViewer";
import Close from "@mui/icons-material/Close";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import uploadIcon from "../../assets/svgIcons/upload.svg";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import { validateDocumentFile, ImageType } from "../../utils/imageValidation";

export interface UploadedFile {
  id: string;
  name: string;
  progress: number;
  status: "uploading" | "uploaded" | "error";
  base64?: string;
  file?: File;
  imageType?: ImageType | "pdf"; // Detected type from magic bytes (image or PDF)
  previewUrl?: string; // Preview URL for valid images
  error?: string; // Error message for invalid files
}
interface BulkUploadModalContentProps {
  onCancel: () => void;
  onContinue: (files: UploadedFile[]) => void;
  tabIndex: number;
  handleDocCount?: (submissionTrackingId: any) => void;
}

const BulkUploadModalContent: React.FC<BulkUploadModalContentProps> = ({
  onCancel,
  onContinue,
  tabIndex,
  handleDocCount,
}) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewDocUrl, setViewDocUrl] = useState<string>("");
  const [viewDocIsPdf, setViewDocIsPdf] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false); 

  const submissionTrackingId: any = useSelector(
    (state: RootState) => state.stepper.trackingId
  );

  // Removed the progress simulation useEffect since we handle conversion directly
  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const selectedFiles = event.target.files;
    
  //       console.log("newFiles",selectedFiles?.length)
  //   if (selectedFiles && selectedFiles.length > 0) {
  //     const processFiles = async () => {
  //       const newFiles: UploadedFile[] = [];
  //       const errors: string[] = [];

  //       for (let i = 0; i < selectedFiles.length; i++) {
  //         const file = selectedFiles[i];
  //         const fileId = `file-${Date.now()}-${i}`;

  //         // Create initial file object
  //         const newFile: UploadedFile = {
  //           id: fileId,
  //           name: file.name,
  //           progress: 0,
  //           status: "uploading" as const,
  //           file: file,
  //         };

  //         // SECURITY: Validate file using magic bytes (file signature)
  //         // We do NOT trust file.name extension or file.type MIME type
  //         const validationResult = await validateImageFile(file);
  //         console.log(newFile,"validationResult", validationResult)
  //         if (!validationResult.isValid) {
  //           // File is not a valid image - reject it and show popup
  //           errors.push(validationResult.error || `File "${file.name}" is not a valid image`);
  //           continue; // Don't add invalid files to the list
  //         }

  //         // File is valid - store the detected type
  //         newFile.imageType = validationResult.type!;

  //         // Create preview URL for valid images
  //         try {
  //           const previewUrl = URL.createObjectURL(file);
  //           newFile.previewUrl = previewUrl;
  //         } catch (error) {
  //           console.error(`Error creating preview for ${file.name}:`, error);
  //         }

  //         // Convert file to base64
  //         try {
  //           const base64 = await convertToBase64(file);
  //           newFile.base64 = base64;
  //           newFile.status = "uploaded" as const;
  //           newFile.progress = 100;
  //         } catch (error) {
  //           console.error(
  //             `Error converting file ${file.name} to base64:`,
  //             error
  //           );
  //           newFile.status = "error" as const;
  //           newFile.error = `Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`;
  //         }

  //         newFiles.push(newFile);
  //       }

  //       // Show validation errors via popup if any
  //       if (errors.length > 0) {
  //         dispatch(
  //           setStatusPopup({
  //             status: "error",
  //             message: `${errors.length} file(s) rejected: ${errors[0]}`,
  //           })
  //         );
  //       }

  //       // Only upload valid files (status === "uploaded")
  //       const validFiles = newFiles.filter((f) => f.status === "uploaded");
  //       if (validFiles.length > 0) {
  //         handleUploadBulkDocuments(validFiles);
  //       }

  //       // Add only valid files to the list for display
  //       setFiles((prev) => [...prev, ...newFiles]);
  //     };

  //     processFiles();
  //   }

  //   // Reset input to allow selecting the same file again
  //   if (fileInputRef.current) {
  //     fileInputRef.current.value = "";
  //   }
  // };
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles:any = event.target.files;
    const errors: string[] = [];

    const newFiles: any = await Promise.all(
      Array.from(selectedFiles).map(async (file:any, i) => {
        const fileId = `file-${Date.now()}-${i}`;

        const newFile: UploadedFile = {
          id: fileId,
          name: file.name,
          progress: 0,
          status: "uploading",
          file,
        };

        const validationResult = await validateDocumentFile(file);

        if (!validationResult.isValid) {
          errors.push(validationResult.error || `File "${file.name}" invalid`);
          return null; // ❌ skip
        }

        newFile.imageType = validationResult.type ?? undefined;

        newFile.previewUrl = URL.createObjectURL(file);

        try {
          newFile.base64 = await convertToBase64(file);
          newFile.status = "uploaded";
          newFile.progress = 100;
        } catch (e) {
          newFile.status = "error";
        }

        return newFile;
      })
    );

    const validFiles = newFiles.filter(Boolean) as any;

    if (validFiles.length) {
      handleUploadBulkDocuments(validFiles); // ✅ all files together
    }

    setFiles(prev => [...prev, ...validFiles]);

    if (errors.length) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: `${errors.length} file(s) rejected`,
        })
      );
    }
  };


  const handleUploadBulkDocuments = async (filesValue: UploadedFile[]) => {
    try {
      setIsUploading(true);

      const arrayOfObj: any = [];
      filesValue.forEach((file: any) => {
        // Use detected imageType from magic bytes validation instead of file.type MIME
        // This ensures we use the real format detected via file signature
        const fileFormat = file?.imageType || file?.file?.type?.split?.("/")?.[1] || "unknown";
        arrayOfObj.push({
          fileName: file?.name,
          fileFormat: fileFormat,
          base64Data: file?.base64,
        });
      });

      const payload = {
        trackingId: submissionTrackingId,
        userType: tabIndex === 0 ? "Applicant" : `Co-Applicant ${tabIndex}`,
        base64Docs: arrayOfObj,
      };

      const response = await eligibilityCheckServices.uploadBulkDocuments(
        payload
      );
      if (response?.status == 200) {
        response?.data?.successes.forEach((file: any) => {
          file.id = file?.id;
          file.status = "uploaded";
          file.progress = 100;
        });
        setFiles([...files, ...response?.data?.successes]);
        
        //handleDocCount && handleDocCount(submissionTrackingId);
        if (response?.data?.failures?.length > 0) {
          dispatch(
            setStatusPopup({
              status: "error",
              message: response?.data?.failures?.[0]?.reason,
            })
          );
        } else {
          dispatch(
            setStatusPopup({
              status: "success",
              message: "Documents uploaded successfully",
            })
          );
        }
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
      console.log(error, "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      // Clean up preview URL before deleting
      const fileToDelete = files.find((f) => f.id === docId);
      if (fileToDelete?.previewUrl) {
        URL.revokeObjectURL(fileToDelete.previewUrl);
      }

      const payload = {
        deleteType: "bulkDoc",
        docId: [Number(docId)],
        trackingId: submissionTrackingId,
      };
      const response = await eligibilityCheckServices.deleteUserDocument(
        payload
      );
      if (response?.status == 200) {
        setFiles((prev) => prev.filter((file) => file.id !== docId));
      }
    } catch (error: any) {
      console.log(error, "error");
    }
  };

  const handlePreviewDocument = async (docPath: string) => {
    setIsModalOpen(true);
    setIsModalLoading(true);

    const payload = {
      key: docPath,
    };
    
    try {
      const response = await eligibilityCheckServices.previewDocument(payload);
      if (response?.status == 200) {
        setViewDocUrl(response?.data?.data?.downloadUrl);
      }
    } catch (error) {
      console.error("Error previewing document", error);
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleShowImageMList = () => {
    return (
      <>
        <div>
          <Grid container spacing={2} sx={{ paddingBottom: "20px" }}>
            <Grid item xs={12}>
              <div
                className="image-container"
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  minWidth: isXsOrSm ? "300px" : "100%",
                  height: isXsOrSm ? "320px" : "80vh",
                  overflow: "auto",
                  marginTop: "39px",
                  backgroundColor: "#fff",
                  position: "relative",
                }}
              >
                {isModalLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: "#00b3b5" }} />
                  </Box>
                ) : (
                  <>
                    {viewDocIsPdf || viewDocUrl?.includes("pdf") || viewDocUrl?.endsWith(".pdf") ? (
                      <PDFViewer pdfUrl={viewDocUrl} />
                    ) : (
                      <img 
                        src={viewDocUrl} 
                        alt="Preview" 
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          console.error("Error loading image preview");
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    );
  };

  const allUploaded =
    files.length > 0 && files.every((f) => f.status === "uploaded");

  // Cleanup preview URLs when component unmounts
  React.useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
  }, []);

  return (
    <Box>
      {isXsOrSm && (
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: "24.2px",
            letterSpacing: "-0.5px",
            color: "black",
            mb: 2,
          }}
        >
          Upload Bulk Documents
        </Typography>
      )}

      {/* Hidden file input */}
      {/* SECURITY NOTE: accept attribute is only for UX convenience.
          We do NOT trust it for security - all files are validated using magic bytes */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,application/pdf,.png,.jpg,.jpeg,.webp,.gif,.pdf"
        style={{ display: "none" }}
      />

      {/* Dropzone Area */}
      <Box
        onClick={handleDropzoneClick}
        sx={{
          border: "2px dashed #e0e0e0",
          borderRadius: "8px",
          p: 2,
          textAlign: "center",
          cursor: "pointer",
          mb: 3,
          bgcolor: "#f9f9f9",
          "&:hover": {
            backgroundColor: "#e0f7fa",
            borderColor: "#00b3b5",
          },
        }}
      >
        {/* <CloudUploadIcon sx={{ fontSize: 48, color: "#00b3b5", mb: 1 }} />{" "} */}
        <img
          src={uploadIcon}
          alt="upload"
          style={{ width: "24px", marginBottom: "2px" }}
        />
        {/* Larger icon */}
        <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333" }}>
          Drop files here or click to upload
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontSize: "0.85rem" }}
        >
          Supported formats: PNG, JPEG, WEBP, GIF, PDF (validated using file signatures)
        </Typography>
      </Box>

      {/* Uploaded Files List */}
      <List sx={{ p: 0 }}>
        {isUploading ? (
          <>
            {/* Skeleton for loading state */}
            {[1].map((index) => (
              <ListItem
                key={`skeleton-${index}`}
                sx={{
                  border: "1px solid #eee",
                  mb: 1,
                  borderRadius: "8px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  {/* File name skeleton */}
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={24}
                    sx={{ bgcolor: "#f0f0f0" }}
                  />

                  {/* Action buttons skeleton */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={32}
                      sx={{ borderRadius: "4px", bgcolor: "#f0f0f0" }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={32}
                      sx={{ borderRadius: "4px", bgcolor: "#f0f0f0" }}
                    />
                  </Box>
                </Box>

                {/* Progress bar skeleton */}
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={8}
                  sx={{ borderRadius: "4px", bgcolor: "#f0f0f0", mb: 0.5 }}
                />

                {/* Progress percentage skeleton */}
                <Skeleton
                  variant="text"
                  width="10%"
                  height={16}
                  sx={{ bgcolor: "#f0f0f0" }}
                />
              </ListItem>
            ))}
          </>
        ) : (
          <>
            {files?.map((file: any) => (
              <ListItem
                key={file.id}
                sx={{
                  border: "1px solid #eee",
                  mb: 2,
                  borderRadius: "8px",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  bgcolor: file.progress === 100 ? "#edfdfdff" : "white",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <TruncatedTextWithTooltip
                      text={String(file?.filename || file?.name || "")}
                      limit={isXsOrSm ? 25 : 35}
                      style={{
                        fontWeight: 500,
                        color: "#333",
                      }}
                    />
                    {file.imageType && file.status === "uploaded" && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#00b3b5",
                          fontSize: "0.75rem",
                          mt: 0.5,
                        }}
                      >
                        {file.imageType === "pdf"
                          ? "PDF document"
                          : `Valid ${file.imageType?.toUpperCase()} image`}
                      </Typography>
                    )}
                  </Box>

                  <ListItemSecondaryAction
                    sx={{
                      position: "static",
                      transform: "none",
                      display: "flex",
                      gap: isXsOrSm ? 0 : 1,
                    }}
                  >
                    {file.previewUrl && file.status === "uploaded" ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                        onClick={() => {
                          setViewDocUrl(file.previewUrl!);
                          setViewDocIsPdf(file.imageType === "pdf");
                          setIsModalOpen(true);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "#00b3b5",
                          minWidth: "auto",
                          px: 1,
                        }}
                      >
                        {isXsOrSm ? "" : "Preview"}
                      </Button>
                    ) : file.s3Url ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                        disabled={file.status !== "uploaded"}
                        onClick={() => handlePreviewDocument(file?.s3Url)}
                        sx={{
                          textTransform: "none",
                          color: "#00b3b5",
                          minWidth: "auto",
                          px: 1,
                        }}
                      >
                        {isXsOrSm ? "" : "View"}
                      </Button>
                    ) : null}
                    <Button
                      color="error"
                      size="small"
                      variant="text"
                      startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleDeleteDocument(file.id)}
                      sx={{
                        textTransform: "none",
                        color: "#ea5455",
                        minWidth: "auto",
                      }}
                    >
                      {isXsOrSm ? "" : "Delete"}
                    </Button>
                  </ListItemSecondaryAction>
                </Box>

                {file.status !== "error" && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={file.progress}
                      sx={{
                        width: "100%",
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#e0e0e0",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: file.status === "error" ? "#ea5455" : "#00b3b5",
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", minWidth: "40px" }}
                    >
                      {file.progress}%
                    </Typography>
                  </Box>
                )}
              </ListItem>
            ))}
          </>
        )}
      </List>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: isXsOrSm ? "space-between" : "center",
          gap: 2,
          mt: 3,
        }}
      >
        {!isXsOrSm && (
          <Button
            onClick={onCancel}
            variant="outlined"
            sx={{
              width: isXsOrSm ? "100%" : "auto",
              textTransform: "none",
              borderColor: "#00b3b5",
              color: "#00b3b5",
              px: 4,
              py: 1,
              borderRadius: "8px",
              "&:hover": {
                borderColor: "#00999a",
                bgcolor: "#e0f7fa",
              },
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          onClick={() => onContinue(files)}
          disabled={!allUploaded || isUploading}
          variant="contained"
          sx={{
            width: isXsOrSm ? "100%" : "auto",
            textTransform: "none",
            bgcolor: "#00b3b5",
            color: "#fff",
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "#00999a",
            },
          }}
        >
          Continue
        </Button>
      </Box>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setViewDocIsPdf(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isXsOrSm ? "300px" : "80%",
            minHeight: isXsOrSm ? "380px" : "",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Close
            onClick={() => {
              setIsModalOpen(false);
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          />
          {handleShowImageMList()}
        </Box>
      </Modal>
    </Box>
  );
};

export default BulkUploadModalContent;
